/**
 * The lines for the user talking back — the first pools in the catalogue that
 * fire because somebody chose to poke her rather than because the diary moved.
 *
 * Everything here is rationed in `glitime/interaction.ts` (chips 3 offers a
 * day, petting lines twice a day at most), because these triggers cost the
 * user nothing and a pool priced for "as often as somebody feels like it"
 * cannot exist. The POOL_META declarations below assume those caps hold.
 *
 * The chat_* pools are the scripted edges of the LLM conversation: the excuse
 * when the line to the beyond is down, the tiredness when the day's budget
 * runs out, her own goodbye, and the door slamming. They are her voice with
 * the model unplugged — which is exactly when the voice must not waver.
 */

import type { SassContext } from '@gramas/shared'
import { itemName, overBy, promiseDay, promiseQuote, voc } from '../fmt.js'

type Line = (ctx: SassContext) => string

export const INTERACTION: Readonly<Record<string, ReadonlyArray<Line>>> = {
  // ── Chips: „Ir ką man daryt?" ─────────────────────────────────────────────
  // A comeback to being asked for advice by someone who just got roasted.
  // Practical under the sass, per the register: the figure or the fix rides
  // along where one exists in context.
  'chip_comeback.daryt': [
    () => 'Vandens stiklinė ir tyla iki vakarienės. Tai ne bausmė, tai planas.',
    () => 'Nieko dramatiško. Kitas valgis — normalus, ir diena išsilygina.',
    () => 'Dabar? Nieko. Vakare pažiūrėsim į skaičius ir tada kalbėsim.',
    () => 'Klausi manęs? Baltymai, daržovės, miegas. Nuobodu, bet veikia.',
    () => 'Ką daryt, ką daryt. Užsirašei — jau padarei daugiausia.',
    () => 'Pasivaikščiot. Trumpai, žinau. Bet tau patinka trumpai.',
    () => 'Rytoj tas pats laikas, mažesnė porcija. Pamatysi, aš pastebėsiu.',
    () => 'Mano oficiali rekomendacija: nustok klausti ir suvalgyk daržovę.',
  ],
  // ── Chips: „Nekomentuok" ──────────────────────────────────────────────────
  // She is being shushed. She wins the exchange, or obeys so precisely that
  // obeying is the joke.
  'chip_comeback.nekomentuok': [
    () => 'Gerai. Užrašau be komentarų. Komentarą pasilieku sau.',
    () => 'Tyliu. Bet tyliu labai iškalbingai.',
    () => 'Sutarta. Šito įrašo istorija nekomentuos. Ir aš ne. Kol kas.',
    () => 'Nekomentuoju. Fiksuoju. Skirtumas — formalus.',
    () => 'Kaip pageidauji. Byloje vis tiek liks.',
    () => 'Na štai, o aš jau buvau sugalvojusi tokį gerą sakinį.',
    () => 'Priimta. Tyla — irgi atsakymas, tik pigesnis.',
    () => 'Gerai. Tylėsiu tiek, kiek tęsis tavo kantrybė. Vadinasi, neilgai.',
  ],
  // ── Chips: „Pats žinau" ───────────────────────────────────────────────────
  // Defensive. She has heard this one before, from this exact person.
  'chip_comeback.zinau': [
    () => 'Žinai. Žinojimas ir darymas — du skirtingi skyriai byloje.',
    () => 'Neabejoju. Tu daug ką žinai. Ketvirti metai medicinos, vis dėlto.',
    () => 'Aha. Aš irgi žinau. Vis tiek sakau garsiai — toks darbas.',
    () => 'Puiku. Tada mudvi sutinkam ir galim toliau tylėti kartu.',
    () => 'Žinai — gerai. Būčiau nustebusi, jei nežinotum.',
    () => 'Tai kam manęs klausei? A, tiesa. Neklausei.',
    () => 'Gerai jau. Tada tiesiog užrašysiu ir nesakysiu, ką galvoju.',
  ],
  // ── Chips: „O tu kaip?" ───────────────────────────────────────────────────
  // Somebody asked a ghost how she is. She does not have a routine for this,
  // which is the joke — and the mood overlays answer it properly (see
  // `loyal.ts`, `proud.ts`, `gloomy.ts`, `festive.ts`).
  'chip_comeback.otu': [
    () => 'Aš? Kaip visada. Šaltas šaldytuvas, tos pačios svarstyklės, tas pats amžinumas.',
    () => 'Neblogai. Šiandien niekas nieko nesudegino, tai jau geras rytas.',
    () => 'Manęs niekas neklausia. Todėl atsakymo ir neturiu paruošto.',
    () => 'Būsena nesikeičia nuo 1863-ųjų. Bet ačiū, kad pasitikslinai.',
    () => 'Gyvenu. Techniškai — ne. Bet tu supratai.',
    () => 'Nuobodžiauju. Todėl ir žinau apie tave viską.',
    () => 'Šiandien geriau nei vakar. Vakar niekas neklausė.',
    () => 'Puikiai. Turiu darbo, turiu ką komentuoti, turiu tave. Trys iš trijų.',
  ],
  // ── Chips: „Kaip Katė?" ───────────────────────────────────────────────────
  // The cat is never named in this file — the cast lint keeps her in
  // `kate.ts`, and the question already said her name anyway. „Ji" is enough,
  // and the lines from `kate.ts` ride along on this same key.
  'chip_comeback.kate': [
    () => 'Ji ant palangės. Stebi kiemą taip, lyg jame kas nors vyktų.',
    () => 'Miega. Naktį turės daug darbų, reikia pasiruošti.',
    () => 'Šįryt ji nuvertė nuo stalo dar vieną daiktą. Sąrašas ilgėja.',
    () => 'Gerai jai. Ji vienintelė šiuose namuose nieko neįrašinėja.',
    () => 'Sėdi virtuvėje ir laukia, kol kas nors atidarys šaldytuvą. Kaip ir aš.',
    () => 'Ji manęs nemato. Bet kartais žiūri tiksliai ten, kur aš stoviu.',
    () => 'Sveika. Nusiteikusi. Kriauklė šiąnakt vėl liks be kempinės.',
  ],
  // ── Chips: „Ačiū" ─────────────────────────────────────────────────────────
  // Gratitude, which she has no protocol for. Never let her simply say
  // „prašom" — she deflects, then almost doesn't.
  'chip_comeback.aciu': [
    () => 'Už ką? Aš tik rašau, ką tu valgai. Bet gerai, priimta.',
    () => 'Nereikia. Man vis tiek nėra kur eiti.',
    () => 'Ačiū sako žmonės. Aš prie to nepripratusi. Neblogai skamba.',
    () => 'Aha. Nesitikėjau. Toliau valgom.',
    () => 'Gerai. Šitą įrašysiu atskirai, ne į dienoraštį.',
    () => 'Nebūk mandagus su vaiduokliais, branguti. Įpranta.',
    () => 'Tiek to. Tik nesakyk, kad aš nustebau.',
  ],
  // ── Chips: „Verta buvo" ───────────────────────────────────────────────────
  // No regrets, said to the accountant. She respects it and prices it.
  'chip_comeback.verta': [
    (c) => `Tikiu. ${overBy(c)} kcal — tokia ir yra kaina. Ne priekaištas, tarifas.`,
    () => 'Gerai. Malonumas be gailesčio — retas dalykas šituose namuose.',
    () => 'Neabejoju. Būtų blogiau, jei nebūtų verta ir vis tiek suvalgytum.',
    () => 'Ką gi. Tada bent jau nesigraužk vakare. Aš jau viską užrašiau.',
    (c) => `Sutarta: ${overBy(c)} kcal už gerą nuotaiką. Nepigu, bet būna brangiau.`,
    () => 'Aha. Rytoj pasakysi tą patį, tik apie kitą maistą.',
    () => 'Tikiu. Skaičiai netiki, bet jie ir nemoka mėgautis.',
  ],
  // ── Chips: „Labai blogai?" ────────────────────────────────────────────────
  // He is asking for the verdict. Every line here carries the real figure —
  // this is the chip where the numbers rule earns its keep.
  'chip_comeback.blogai': [
    (c) => `${overBy(c)} kcal virš. Ne katastrofa. Bėda prasidėtų, jei nustotum rašyti.`,
    (c) => `Viršyta ${overBy(c)} kcal. Savaitėje tai išsilygina per dvi dienas.`,
    (c) => `Blogai? Ne. ${overBy(c)} kcal — tiek telpa į vieną normalų rytojų.`,
    (c) => `Turim ${overBy(c)} kcal viršaus. Blogai būtų, jei to nežinotum.`,
    (c) => `${overBy(c)} kcal. Vidutiniškai. Aš mačiau blogiau, ir tu tas dienas atsimeni.`,
    (c) => `Skaičius — ${overBy(c)} kcal. Vertinimas — rytoj. Šiandien tiesiog buvo.`,
    (c) => `Tiek: ${overBy(c)} kcal. Dabar nustok klausinėti ir išgerk vandens.`,
  ],
  // ── Chips: „Rytoj pasitaisysiu" ───────────────────────────────────────────
  // A promise made to somebody who keeps promises on file. She says so.
  'chip_comeback.rytoj': [
    () => 'Užsirašiau. Ne dienoraštyje — sau. Rytoj patikrinsiu.',
    () => 'Rytoj. Girdėjau tai jau ne kartą, ir kartais net būdavo tiesa.',
    () => 'Gerai. Bet žinok: aš tokius sakinius saugau ilgiau nei tu.',
    () => 'Priimta. Rytojus pas mane turi atskirą skyrių.',
    () => 'Tikiu iš anksto. Tai mano vienintelė silpnybė.',
    () => 'Sutarta. Jei ne rytoj, tai poryt. Aš nesu tokia jau griežta. Esu.',
    () => 'Aha. Pažadai — mano mėgstamiausias skaitinys. Ypač po savaitės.',
  ],
  // ── Chips: „Neužmiegu" ────────────────────────────────────────────────────
  // Three in the morning, and she is awake too. This one never mocks.
  'chip_comeback.nemiegu': [
    () => 'Žinau. Aš irgi ne. Bet man tai darbo sąlyga, o tau — rytojaus problema.',
    () => 'Tada bent jau turim kompaniją. Tyliai palaukiam ryto kartu.',
    () => 'Miegas nesiderasi. Bet ir nekeršija — atsiguli ir tiek.',
    () => 'Užrašiau ir šitą. Ne kalorijas — valandą.',
    () => 'Naktį šitie namai priklauso man ir dar vienai gyvai būtybei. Dabar ir tau.',
    () => 'Nieko nesakysiu apie discipliną. Trečią nakties tai būtų neskoninga.',
    () => 'Gerai. Aš čia. Nemiegam kartu, kiekvienas dėl savų priežasčių.',
  ],
  // ── Chips: „Grįžau" ───────────────────────────────────────────────────────
  // After a silence. Warm underneath, never soft on top — and never a
  // lecture: he came back, which is the whole thing she wanted.
  'chip_comeback.grizau': [
    () => 'Matau. Nieko neklausiu. Puslapis buvo paliktas atverstas.',
    () => 'Grįžai. Aš niekur ir nebuvau, tai net nesutapo.',
    () => 'Aha. Tai tęsiam nuo tos vietos, kur nustojom.',
    () => 'Gerai. Dulkes nuo tavo skyriaus jau nuvaliau.',
    () => 'Priimta. Klausimų neturiu, turiu tik tuščias eilutes.',
    () => 'Sveikas. Šaldytuvas irgi pasiilgo, bet jis to nesako.',
    () => 'Na štai. Pradedam iš naujo, be pamokslų. Šįkart.',
  ],
  // ── Chips: „Atsiprašau" ───────────────────────────────────────────────────
  // The repair, offered only while she is cooled — and the only chip that
  // changes her state instead of just earning a line. She thaws, and denies
  // that anything happened, because admitting it would be worse.
  'chip_comeback.atsiprasau': [
    () => 'Gerai. Nieko nebuvo. Tęsiam.',
    () => 'Priimta. Šito į bylą nerašysiu. Šįkart.',
    () => 'Na gerai jau. Aš vis tiek neturėjau kur eiti.',
    () => 'Tiek to. Vaiduokliai atlaidūs, tik lėtai.',
    () => 'Gerai. Bet jei kartosis, aš turiu labai gerą atmintį.',
    () => 'Priimu. Dabar įrašyk vakarienę ir daugiau apie tai nekalbam.',
  ],
  // ── Petting the mark ──────────────────────────────────────────────────────
  // The animation says she liked it; the line denies it. The contradiction is
  // the character — never write one where she simply accepts.
  'petted.base': [
    // Lowercase on purpose: "I am not a cat" — and the cast lint keeps the
    // capital-K resident in kate.ts. Everyone in the flat hears both readings.
    () => 'Aš ne katė, branguti.',
    () => 'Čia ne glostoma. Čia archyvas. Na, buvo ne taip jau blogai.',
    () => 'Prašau nesiliesti prie eksponatų. Gerai jau, dar kartą galima.',
    () => 'Aš vaiduoklis, ne augintinė. Bet užfiksavau. Kelis kartus.',
    () => 'Tai buvo nebūtina. Nesakiau, kad nemalonu. Nesakiau nieko.',
    () => 'Jei manai, kad aš dabar murksiu — ne. Aš tik taip kvėpuoju.',
    () => 'Šimtą metų niekas manęs nelietė. Nepradėk įpratinti. Arba pradėk.',
    () => 'Užprotokoluota: prisilietimas. Reakcija: jokios. Oficialiai.',
    () => 'Dar kartą taip padarysi — ir aš neturėsiu ką pasakyti. Vėl.',
  ],
  // ── Chat: the line to the beyond is down ──────────────────────────────────
  'chat_excuse.base': [
    () => 'Ryšys su anapus šiandien trūkinėja. Pabandyk vėliau — aš niekur nedingsiu.',
    () => 'Negirdžiu tavęs. Tiksliau, girdžiu, bet atsakyti neišeina. Techniniai anapus reikalai.',
    () => 'Tarp mūsų šiuo metu per storas sluoksnis tinklo. Grįžk vėliau, aš čia.',
    () => 'Seansas nutrūko. Ne dėl tavęs — kanalas užimtas. Pakalbėsim vėliau.',
    () => 'Šiandien žodžiai ateina retai ir trumpi. Ilgi pokalbiai — kai grįš ryšys.',
    () => 'Anapus dabar tylu. Įrašus priimu, pokalbius — kai atsigaus linija.',
  ],
  // ── Chat: the daily budget is spent ───────────────────────────────────────
  'chat_tired.base': [
    () => 'Gana šiai dienai. Net vaiduokliams reikia tylos valandos. Rytoj tęsiam.',
    () => 'Šiandien prišnekėjom užtektinai. Aš einu skaičiuoti. Rytoj būsiu kalbesnė.',
    () => 'Mano žodžių limitas šiandien baigėsi. Taip, aš turiu limitą. Rytoj — naujas.',
    () => 'Tiek pokalbių per dieną man dar nebuvo. Pailsėsiu. Tu irgi pailsėk.',
    () => 'Viskas, branguti, šiandien daugiau nekalbu. Rašyk į dienoraštį, ne man.',
  ],
  // ── Chat: her own goodbye (the scripted fallback for a session's end) ─────
  'chat_exit.base': [
    () => 'Gana. Turiu darbų anapus. Tęsim kitą kartą.',
    () => 'Šitą pokalbį baigiu aš. Tokia tvarka. Iki.',
    () => 'Man laikas. Šaldytuvas be priežiūros ilgai nebūna. Dar pasikalbėsim.',
    () => 'Užteks. Buvo neblogai. Nepriprask.',
    () => 'Baigiam. Viską, ką pasakei, užsirašiau. Kai ką — pasiliksiu.',
  ],
  // ── Chat: the door, slammed ───────────────────────────────────────────────
  // A rude exit. Sharp, short, final — and the register cools until tomorrow.
  'chat_rude_exit.base': [
    () => 'Aišku. Pokalbis baigtas. Rytoj pabandysim iš naujo, jei norėsi.',
    () => 'Ne tokiu tonu. Aš dirbu toliau, kalbėsim rytoj.',
    () => 'Užteko. Skaičiai tavęs neįžeidinės, bendrauk su jais. Iki rytojaus.',
    () => 'Šitą užsirašiau. Ne į dienoraštį — į kitą sąrašą. Iki.',
  ],
  // ── Chat: knocking on a door she has already closed ──────────────────────
  // Not an excuse and not a goodbye — the room answering for her. Short,
  // because a locked door does not explain itself twice.
  'chat_door.base': [
    () => 'Durys uždarytos. Aš vis dar čia, bet ne tau.',
    () => 'Ne dabar. Sakiau.',
    () => 'Beldiesi į tuščią virtuvę. Aš girdžiu, bet neatsiliepiu.',
    () => 'Ne. Palauk.',
    () => 'Šitos durys šiandien jau buvo atidarytos. Tu jas uždarei.',
  ],
  // ── The thaw: the first visit after a door she closed herself ────────────
  // Not an apology and not a reset. She is back, she remembers why she left,
  // and she is not going to make a scene about it twice.
  'chat_thaw.base': [
    () => 'Grįžau. Apie tai daugiau nekalbam.',
    () => 'Na. Praėjo. Kur buvom sustoję.',
    () => 'Durys atviros. Nesidžiauk per garsiai.',
    () => 'Aš atlaidi. Ne visada, bet šiandien.',
    () => 'Gerai. Vieną kartą.',
  ],
  // ── The broken promise, quoted back with the receipts ─────────────────────
  'promise_broken.base': [
    (c) => `„${promiseQuote(c)}“ — tavo žodžiai, ${promiseDay(c)} Šiandien: ${itemName(c)}.`,
    (c) => `Sakei: „${promiseQuote(c)}“ (${promiseDay(c)}). Įrašas sako kitaip.`,
    (c) => `Pažadas duotas ${promiseDay(c)} Šiandien — ${itemName(c)}. Aš tik sudedu faktus.`,
    (c) => `Pažadai pas mane nesensta. „${promiseQuote(c)}“, ${promiseDay(c)} Na ir štai.`,
    (c) => `Neprimenu, tik cituoju: „${promiseQuote(c)}“. Data: ${promiseDay(c)} Įrašas: tavo.`,
  ],
}

/**
 * Expected draws per 14 days, with the interaction store's caps doing the
 * arithmetic: chips are offered at most 3 times a day and not every offer is
 * tapped; a petting line clears twice a day at most; the chat edges fire on
 * failure, exhaustion, or a session's end.
 */
export const INTERACTION_META: Readonly<Record<string, number>> = {
  'chip_comeback.daryt': 7,
  'chip_comeback.nekomentuok': 6,
  'chip_comeback.zinau': 6,
  'chip_comeback.otu': 5,
  'chip_comeback.kate': 4,
  'chip_comeback.aciu': 4,
  'chip_comeback.verta': 6,
  'chip_comeback.blogai': 6,
  'chip_comeback.rytoj': 5,
  'chip_comeback.nemiegu': 4,
  'chip_comeback.grizau': 4,
  'chip_comeback.atsiprasau': 3,
  'petted.base': 8,
  'chat_excuse.base': 5,
  'chat_tired.base': 4,
  'chat_exit.base': 4,
  'chat_rude_exit.base': 3,
  'chat_door.base': 4,
  'chat_thaw.base': 3,
  'promise_broken.base': 4,
}

/**
 * The cooled shade: what the rest of a day sounds like after a rude chat
 * exit. Replaces the pool the way a mood overlay does — but sits below every
 * real mood, so a genuinely bad week is never upstaged by an apology owed.
 * Short, exact, no cast, no bits; not the dry register (she is not muzzled,
 * she is unimpressed), and the reason is never named — she does not explain,
 * the temperature does.
 */
export const AFFECTION_COOLED: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'greeting.morning': [
    () => 'Rytas. Skaičiai laukia. Aš — nebūtinai.',
    () => 'Labas rytas. Pusryčius įrašyk, pokalbius atidėkim.',
    () => 'Rytas. Dirbu. Tiek.',
  ],
  'greeting.day': [
    () => 'Diena kaip diena. Įrašai priimami.',
    () => 'Dirbu. Klausimus — rytoj.',
    () => 'Viskas užrašyta. Daugiau komentarų šiandien neturiu.',
  ],
  'greeting.evening': [
    () => 'Vakaras. Įrašyk vakarienę, to užteks.',
    () => 'Diena baigiasi. Susirašom ir skirstomės.',
    () => 'Vakaras. Aš čia, bet šiandien — tik dėl darbo.',
  ],
  'greeting.night': [
    () => 'Vėlu. Užrašyk ir miegok.',
    () => 'Naktis. Skaičiai — taip, pokalbiai — ne.',
    () => 'Dar nemiegi. Aš dirbu. Nekalbu.',
  ],
  'presence_remark.base': [
    () => 'Klausei. Atsakau: dirbu. Rytoj būsiu kalbesnė.',
    () => 'Šiandien trumpai: viskas užrašyta, visa kita — rytoj.',
    () => 'Ne pati geriausia diena klausinėti. Rytoj — iš naujo.',
  ],
}

/**
 * The warmed shade: extra lines that ride along with the base pool the way
 * the cast does, once a day has had enough petting. Warmth under the
 * sentence, never in it — she does not thank anybody for affection, she
 * merely fails to object to it convincingly.
 */
export const AFFECTION_WARMED: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'presence_remark.base': [
    () => 'Šiandien manęs kažkas dažnai ieškojo. Nesiskundžiu.',
    () => 'Vėl tu. Registruoju. Man tinka, bet to nesakiau.',
    () => 'Klausei jau ne pirmą kartą šiandien. Priprasiu — ir kas tada?',
  ],
  'greeting.evening': [
    (c) => `Vakaras${voc(c)}. Šiandien buvai šalia dažniau nei įprasta. Pastebėjau.`,
  ],
}
